import { useEffect, useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { TooltipButton } from './TooltipButton';
import { BoundaryPendingMark } from './BoundaryPendingMark';
import './reader-neighbour.css';

/** How much of the page before shows when a document opens: about an inch, so it is seen and this document fills the rest. */
export const NEIGHBOUR_PEEK = 96;

/**
 * Page 1 of a document with the page before peeking in above it (owner, 2026-10-30: "the bottom inch of the
 * previous page, just so they know it's there"). False when there is no page before to show.
 */
export function scrollToNeighbourSeam(viewer: HTMLElement) {
  const gap = viewer.querySelector<HTMLElement>('.reader-neighbour.before .reader-document-gap');
  if (!gap) return false;
  viewer.scrollTop +=
    gap.getBoundingClientRect().top - viewer.getBoundingClientRect().top - NEIGHBOUR_PEEK;
  return true;
}

/**
 * A neighbouring document's page in the reader (owner and Dustin, 2026-10-30; every-document-calibration.md):
 * the page before above this document's first page, the next document's first page below its last. Full size
 * and never dimmed (the page is evidence). A neighbour with nothing left to do wears the list's calm green
 * check, kept in the part of the page on screen. Between it and this document, a gap wider than a page break
 * and a wordless paperclip, the list's own join, its words in the tooltip; it is offered only once the page has
 * drawn, so a join is always made with both pages in view.
 */
export function ReaderNeighbour({
  side,
  width,
  label,
  done,
  toExceptions = false,
  previewPath,
  pixelWidth,
  pixelHeight,
  pending,
  joinTooltip,
  joinLabel,
  onJoin,
}: {
  side: 'before' | 'after';
  width: string;
  /** What the page is, for assistive technology ("Last page of Invoice, document 2"). */
  label: string;
  /** The neighbour has nothing left to do: it wears the check. */
  done: boolean;
  /**
   * The neighbour goes to the exceptions folder (ADR-0096): nothing left for a person, but not done, so a neutral
   * "Goes to exceptions" note in place of the check (owner, 2026-10-31).
   */
  toExceptions?: boolean;
  previewPath: string;
  pixelWidth?: number;
  pixelHeight?: number;
  /** A join at this gap accepted and still saving. */
  pending?: 'split' | 'join' | 'undo' | null;
  joinTooltip: string;
  /** The paperclip's name, as the list names the same control ("Merge document 3 with previous"). */
  joinLabel: string;
  onJoin: () => void;
}) {
  const [loaded, setLoaded] = useState<{ path: string; source: string | null } | null>(null);
  // The shape of the image actually shown: a turned page's image is not the shape of the page as read.
  const [shape, setShape] = useState<{ path: string; ratio: number } | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let disposed = false;
    const desktop = window.capturePointDesktop;
    if (!desktop) return;
    desktop
      .readPagePreview(previewPath)
      .then((source) => {
        if (!disposed) setLoaded({ path: previewPath, source });
      })
      .catch(() => {
        if (!disposed) setLoaded({ path: previewPath, source: null });
      });
    return () => {
      disposed = true;
    };
  }, [previewPath]);
  const read = loaded?.path === previewPath ? loaded : null;
  const shown = Boolean(read?.source) && shape?.path === previewPath;
  // The check sits in the middle of the part of the page on screen, sized to fit it, so the inch that peeks in
  // above page 1 still carries it. Follows the scroll a frame at a time; nothing when there is no check.
  useEffect(() => {
    const page = pageRef.current;
    const viewer = page?.closest<HTMLElement>('.paper-canvas');
    if (!done || !shown || !page || !viewer) return;
    let frame = 0;
    const place = () => {
      frame = 0;
      const box = page.getBoundingClientRect(),
        view = viewer.getBoundingClientRect();
      const top = Math.max(box.top, view.top),
        bottom = Math.min(box.bottom, view.bottom);
      if (bottom - top <= 0) return;
      const radius = Math.max(
        14,
        Math.min(0.17 * Math.min(box.width, box.height), (bottom - top - 20) / 2),
      );
      page.style.setProperty('--page-cover-y', `${(top + bottom) / 2 - box.top}px`);
      page.style.setProperty('--page-cover-r', `${radius}px`);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(place);
    };
    place();
    viewer.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(schedule);
    observer.observe(page);
    observer.observe(viewer);
    return () => {
      viewer.removeEventListener('scroll', schedule);
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [done, shown]);
  const ratio =
    shape?.path === previewPath
      ? shape.ratio
      : pixelWidth && pixelHeight && pixelWidth > 1
        ? pixelHeight / pixelWidth
        : 11 / 8.5;
  // Not paper to pan or select on: a press here is the page's or the paperclip's.
  const keepPress = (event: React.PointerEvent) => {
    if (event.button === 0) event.stopPropagation();
  };
  const page = (
    <div
      ref={pageRef}
      className={`reader-neighbour-page${done && shown ? ' page-covered' : ''}`}
      role="img"
      aria-label={label}
      style={{ aspectRatio: `1 / ${ratio.toFixed(4)}` }}
    >
      {read?.source ? (
        <img
          src={read.source}
          alt=""
          draggable={false}
          onLoad={(event) => {
            const image = event.currentTarget;
            if (image.naturalWidth > 0)
              setShape({ path: previewPath, ratio: image.naturalHeight / image.naturalWidth });
          }}
        />
      ) : read ? (
        <span className="reader-neighbour-failed" role="status">
          This page could not be shown.
        </span>
      ) : (
        <span className="reader-neighbour-loading" aria-hidden="true" />
      )}
      {toExceptions && !done && <span className="reader-neighbour-note">Goes to exceptions</span>}
    </div>
  );
  const gap = (
    <div className="reader-document-gap">
      {pending ? (
        <BoundaryPendingMark kind={pending} />
      ) : (
        <TooltipButton
          type="button"
          className="reader-join-action"
          data-telemetry="document-join"
          data-join-from={side === 'before' ? 'reader-before' : 'reader-after'}
          disabled={!shown}
          tooltip={joinTooltip}
          aria-label={joinLabel}
          onClick={onJoin}
        >
          <Paperclip size={16} aria-hidden="true" />
        </TooltipButton>
      )}
    </div>
  );
  return (
    <div className={`reader-neighbour ${side}`} style={{ width }} onPointerDown={keepPress}>
      {side === 'before' ? (
        <>
          {page}
          {gap}
        </>
      ) : (
        <>
          {gap}
          {page}
        </>
      )}
    </div>
  );
}
